import React from 'react';
import { Box, Text, Button, PlusIcon } from '@razorpay/blade/components';
import { AIWorkflowConfig, WorkflowStep, StepType } from '../../types';
import { WorkflowContainer, SectionWrapper, DottedBackground } from './styles';
import { StepCard } from './StepCard';
import { ConnectorWithArrow } from './ConnectorWithArrow';

interface WorkflowCanvasProps {
  workflow: AIWorkflowConfig;
  selectedStep?: WorkflowStep;
  onStepClick: (step: WorkflowStep | null, type: StepType) => void;
  onAddStep: (type: StepType, afterStepId?: string) => void;
  onAddDecision: () => void;
  onDeleteStep: (step: WorkflowStep) => void;
}

const WorkflowCanvas = ({
  workflow,
  selectedStep,
  onStepClick,
  onAddStep,
  onAddDecision,
  onDeleteStep,
}: WorkflowCanvasProps) => {
  const hasDecision = workflow.decision !== null;
  const hasTrigger = workflow.trigger !== null;

  const triggerStepNumber = 1;
  const decisionStepNumber = 2;
  const actionStartStepNumber = hasDecision ? 3 : 2;

  return (
    <DottedBackground>
      <WorkflowContainer>
        {/* Starter Section */}
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          width="100%"
          marginBottom="spacing.3">
          <Box marginBottom="spacing.2">
            <Text size="small" color="surface.text.gray.muted" weight="semibold">
              Starter
            </Text>
          </Box>
          <StepCard
            step={workflow.trigger || undefined}
            stepNumber={triggerStepNumber}
            type="trigger"
            isSelected={selectedStep?.type === 'trigger' && selectedStep?.id === workflow.trigger?.id}
            onClick={() => onStepClick(workflow.trigger, 'trigger')}
            isPlaceholder={!workflow.trigger}
            placeholderText="Choose a starter"
          />
        </Box>

        <ConnectorWithArrow />

        {/* Actions Section */}
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          width="100%">
          <Box marginBottom="spacing.2">
            <Text size="small" color="surface.text.gray.muted" weight="semibold">
              Actions
            </Text>
          </Box>

          {hasDecision && workflow.decision ? (
            <>
              <StepCard
                step={workflow.decision}
                stepNumber={decisionStepNumber}
                type="decision"
                isSelected={selectedStep?.type === 'decision'}
                onClick={() => onStepClick(workflow.decision, 'decision')}
                hasHighlight={selectedStep?.type === 'decision'}
              />

              <ConnectorWithArrow />

              <SectionWrapper isNested>
                {workflow.actions.map((action, index) => (
                  <Box key={action.id} display="flex" flexDirection="column" alignItems="center" width="100%">
                    {index > 0 && <ConnectorWithArrow />}
                    <StepCard
                      step={action}
                      stepNumber={actionStartStepNumber + index}
                      type="action"
                      isSelected={selectedStep?.id === action.id}
                      onClick={() => onStepClick(action, 'action')}
                      onDelete={() => onDeleteStep(action)}
                      isDeletable={true}
                    />
                  </Box>
                ))}

                {workflow.actions.length > 0 && <ConnectorWithArrow />}
                <StepCard
                  stepNumber={actionStartStepNumber + workflow.actions.length}
                  type="action"
                  isPlaceholder
                  placeholderText="Choose a step"
                  onClick={() => onAddStep('action')}
                  showMenu={false}
                />

                <Box marginTop="spacing.3">
                  <Button
                    variant="tertiary"
                    size="small"
                    icon={PlusIcon}
                    onClick={() => onAddStep('action')}>
                    Add substep
                  </Button>
                </Box>
              </SectionWrapper>
            </>
          ) : (
            <>
              {hasTrigger && !hasDecision && (
                <Box marginBottom="spacing.3">
                  <Button
                    variant="tertiary"
                    size="small"
                    icon={PlusIcon}
                    onClick={onAddDecision}>
                    Add AI Decision (optional)
                  </Button>
                </Box>
              )}

              {workflow.actions.map((action, index) => (
                <Box key={action.id} display="flex" flexDirection="column" alignItems="center" width="100%">
                  {index > 0 && <ConnectorWithArrow />}
                  <StepCard
                    step={action}
                    stepNumber={actionStartStepNumber + index}
                    type="action"
                    isSelected={selectedStep?.id === action.id}
                    onClick={() => onStepClick(action, 'action')}
                    onDelete={() => onDeleteStep(action)}
                    isDeletable={true}
                  />
                </Box>
              ))}

              {workflow.actions.length > 0 && <ConnectorWithArrow />}
              <StepCard
                stepNumber={actionStartStepNumber + workflow.actions.length}
                type="action"
                isPlaceholder
                placeholderText="Choose a step"
                onClick={() => onAddStep('action')}
                showMenu={false}
              />

              <Box marginTop="spacing.3">
                <Button
                  variant="tertiary"
                  size="small"
                  icon={PlusIcon}
                  onClick={() => onAddStep('action')}>
                  Add substep
                </Button>
              </Box>
            </>
          )}
        </Box>
      </WorkflowContainer>
    </DottedBackground>
  );
};

export { WorkflowCanvas };
